import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FiTrendingUp } from "react-icons/fi";
import CrudDetailPage, { DetailSection, DetailField, DetailGrid } from "../../../components/crud/CrudDetailPage";
import FormGrid, { FormField, FormSection } from "../../../components/crud/FormGrid";
import { formatCurrency } from "../../../components/crud/DataTable";
import { getSubcategoryOptionsByBusiness } from "../../utils/spendingCategories";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

const BudgetItemEdit = () => {
	const [item, setItem] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [dynamicCategories, setDynamicCategories] = useState({}); // Categories pulled from line items for NL/Immerge

	// Edit form state
	const [formData, setFormData] = useState({
		section: "",
		category: "",
		name: "",
		amount: "",
		is_income: false,
		start_date: "",
		end_date: "",
		specific_months: [],
		live_category_ref: [],
		sort_order: 0,
		is_large_ticket: false,
	});

	const location = useLocation();
	const id = location.pathname.split("/")[2];
	const isNew = id === "new";

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (!isNew) {
					const res = await axios.get(`${backendUrl}/budget/items/${id}`);
					const data = res.data;
					setItem(data);

					// Parse specific_months if it's a string
					let months = data.specific_months;
					if (typeof months === "string") {
						months = JSON.parse(months);
					}

					// Parse live_category_ref as JSON array
					let categoryRefs = data.live_category_ref;
					if (typeof categoryRefs === "string" && categoryRefs) {
						try {
							categoryRefs = JSON.parse(categoryRefs);
						} catch {
							categoryRefs = [];
						}
					}

					setFormData({
						section: data.section || "",
						category: data.category || "",
						name: data.name || "",
						amount: Math.abs(data.amount) || "",
						is_income: data.is_income || false,
						start_date: data.start_date ? data.start_date.split("T")[0] : "",
						end_date: data.end_date ? data.end_date.split("T")[0] : "",
						specific_months: months || [],
						live_category_ref: categoryRefs || [],
						sort_order: data.sort_order || 0,
						is_large_ticket: data.is_large_ticket || false,
					});
				}
			} catch (err) {
				console.error("Error fetching budget item:", err);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [id, isNew]);

	// Fetch line items to extract unique categories for NL and Immerge
	useEffect(() => {
		const fetchLineItemCategories = async () => {
			try {
				const res = await axios.get(`${backendUrl}/money/lineItems`);
				const lineItems = res.data;

				// Extract unique categories for NL and Immerge
				const categoryMap = { NL: new Set(), Immerge: new Set() };
				lineItems.forEach((item) => {
					if (item.business === "NL" && item.category) {
						categoryMap.NL.add(item.category);
					} else if (item.business === "Immerge" && item.category) {
						categoryMap.Immerge.add(item.category);
					}
				});

				// Convert to the same format as getSubcategoryOptionsByBusiness
				const dynamic = {};
				["NL", "Immerge"].forEach((business) => {
					const categories = Array.from(categoryMap[business]).sort();
					if (categories.length > 0) {
						dynamic[business] = {
							"All Categories": categories.map((cat) => ({
								value: `${business}:${cat}`,
								label: cat,
							})),
						};
					}
				});

				setDynamicCategories(dynamic);
			} catch (err) {
				console.error("Error fetching line item categories:", err);
			}
		};
		fetchLineItemCategories();
	}, []);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleMonthToggle = (month) => {
		setFormData((prev) => {
			const months = prev.specific_months || [];
			if (months.includes(month)) {
				return { ...prev, specific_months: months.filter((m) => m !== month) };
			} else {
				return { ...prev, specific_months: [...months, month].sort((a, b) => a - b) };
			}
		});
	};

	const handleCategoryRefToggle = (categoryRef) => {
		setFormData((prev) => {
			const refs = prev.live_category_ref || [];
			if (refs.includes(categoryRef)) {
				return { ...prev, live_category_ref: refs.filter((r) => r !== categoryRef) };
			} else {
				return { ...prev, live_category_ref: [...refs, categoryRef] };
			}
		});
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			// Prepare data - make amount negative for expenses
			const saveData = {
				...formData,
				amount: formData.is_income ? Math.abs(formData.amount) : -Math.abs(formData.amount),
				specific_months: formData.specific_months.length > 0 ? formData.specific_months : null,
				start_date: formData.start_date || null,
				end_date: formData.end_date || null,
				live_category_ref: formData.live_category_ref.length > 0 ? JSON.stringify(formData.live_category_ref) : null,
			};

			if (isNew) {
				const res = await axios.post(`${backendUrl}/budget/items`, saveData);
				window.location.href = `/budget-items/${res.data.id}`;
			} else {
				await axios.put(`${backendUrl}/budget/items/${id}`, saveData);
				const res = await axios.get(`${backendUrl}/budget/items/${id}`);
				setItem(res.data);
			}
		} catch (err) {
			console.error("Error saving budget item:", err);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <div style={{ padding: "20px" }}>Loading...</div>;
	}

	// Common sections (used across Personal, Business, etc.)
	const commonSections = ["Personal", "Business", "Income", "Child", "Savings", "Other"];

	// Month selector component
	const MonthSelector = () => {
		const months = [
			{ num: 1, name: "Jan" },
			{ num: 2, name: "Feb" },
			{ num: 3, name: "Mar" },
			{ num: 4, name: "Apr" },
			{ num: 5, name: "May" },
			{ num: 6, name: "Jun" },
			{ num: 7, name: "Jul" },
			{ num: 8, name: "Aug" },
			{ num: 9, name: "Sep" },
			{ num: 10, name: "Oct" },
			{ num: 11, name: "Nov" },
			{ num: 12, name: "Dec" },
		];

		return (
			<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
				{months.map((m) => (
					<button key={m.num} type="button" onClick={() => handleMonthToggle(m.num)} className={`l-btn sm ${formData.specific_months?.includes(m.num) ? "primary" : "ghost"}`} style={{ minWidth: "50px" }}>
						{m.name}
					</button>
				))}
				<button type="button" onClick={() => setFormData((prev) => ({ ...prev, specific_months: [] }))} className="l-btn sm ghost" style={{ marginLeft: "auto" }}>
					Clear All
				</button>
			</div>
		);
	};

	// Edit form content
	const editForm = (
		<FormGrid>
			<FormField label="Section" name="section" width="third" required>
				<select name="section" value={formData.section} onChange={handleChange} required>
					<option value="">Select Section...</option>
					{commonSections.map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</select>
			</FormField>

			<FormField label="Category" name="category" width="third">
				<input type="text" name="category" value={formData.category} onChange={handleChange} placeholder="e.g., Housing, Food, Office" />
			</FormField>

			<FormField label="Name" name="name" width="third" required>
				<input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="e.g., Rent, Groceries" required />
			</FormField>

			<FormField label="Monthly Amount ($)" name="amount" width="quarter" required>
				<input type="number" name="amount" value={formData.amount} onChange={handleChange} step="0.01" min="0" required />
			</FormField>

			<FormField label="Type" name="is_income" width="quarter">
				<div style={{ display: "flex", gap: "16px", paddingTop: "8px" }}>
					<label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
						<input type="radio" name="is_income" checked={!formData.is_income} onChange={() => setFormData((prev) => ({ ...prev, is_income: false }))} />
						Expense
					</label>
					<label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
						<input type="radio" name="is_income" checked={formData.is_income} onChange={() => setFormData((prev) => ({ ...prev, is_income: true }))} />
						Income
					</label>
				</div>
			</FormField>

			<FormField label="Sort Order" name="sort_order" width="quarter">
				<input type="number" name="sort_order" value={formData.sort_order} onChange={handleChange} min="0" />
			</FormField>

			<FormSection title="Recurrence (Optional)" />

			<FormField label="Start Date" name="start_date" width="half">
				<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
					<input type="date" name="start_date" value={formData.start_date} onChange={handleChange} style={{ flex: 1 }} />
					{formData.start_date && (
						<button type="button" className="l-btn sm ghost" onClick={() => setFormData((prev) => ({ ...prev, start_date: "" }))}>
							Clear
						</button>
					)}
				</div>
			</FormField>

			<FormField label="End Date" name="end_date" width="half">
				<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
					<input type="date" name="end_date" value={formData.end_date} onChange={handleChange} style={{ flex: 1 }} />
					{formData.end_date && (
						<button type="button" className="l-btn sm ghost" onClick={() => setFormData((prev) => ({ ...prev, end_date: "" }))}>
							Clear
						</button>
					)}
				</div>
			</FormField>

			<FormField label="Specific Months (leave empty for all months)" name="specific_months" width="full">
				<MonthSelector />
			</FormField>

			<FormSection title="Live Spending Tracking" />

			<FormField label="Link to Spending Categories" name="live_category_ref" width="full">
				<p style={{ margin: "0 0 12px", fontSize: "1.2rem", color: "#666" }}>
					Select spending categories to track this budget item against actual spending.
				</p>
				<div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
					{/* Merge static categories with dynamically loaded NL/Immerge categories */}
					{Object.entries({ ...getSubcategoryOptionsByBusiness(), ...dynamicCategories }).map(([business, groups]) => (
						<div key={business}>
							<div style={{ fontWeight: 700, fontSize: "1.3rem", marginBottom: "12px", color: "#333", borderBottom: "1px solid #ddd", paddingBottom: "4px" }}>{business}</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
								{Object.entries(groups).map(([groupName, categories]) => (
									<div key={groupName}>
										<div style={{ fontWeight: 500, fontSize: "1.1rem", marginBottom: "6px", color: "#666" }}>{groupName}</div>
										<div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
											{categories.map((cat) => {
												const isSelected = formData.live_category_ref?.includes(cat.value);
												return (
													<button
														key={cat.value}
														type="button"
														onClick={() => handleCategoryRefToggle(cat.value)}
														className={`l-btn sm ${isSelected ? "primary" : "ghost"}`}
													>
														{cat.label}
													</button>
												);
											})}
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
				{formData.live_category_ref?.length > 0 && (
					<div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
						<span style={{ fontSize: "1.1rem", color: "#666" }}>Selected: {formData.live_category_ref.length}</span>
						<button type="button" onClick={() => setFormData((prev) => ({ ...prev, live_category_ref: [] }))} className="l-btn sm ghost">
							Clear All
						</button>
					</div>
				)}
			</FormField>

			{formData.live_category_ref?.length > 0 && (
				<FormField label="Large Ticket Item" name="is_large_ticket" width="full">
					<label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer" }}>
						<input
							type="checkbox"
							checked={formData.is_large_ticket || false}
							onChange={(e) => setFormData((prev) => ({ ...prev, is_large_ticket: e.target.checked }))}
							style={{ marginTop: "4px", width: "18px", height: "18px" }}
						/>
						<div>
							<div style={{ fontWeight: 500, marginBottom: "4px" }}>Check actual payment instead of proration</div>
							<div style={{ fontSize: "1.1rem", color: "#666" }}>
								For items like rent or insurance: if paid this month, show $0 remaining. If paid in advance (e.g., 2 months), show $0 for all covered months.
							</div>
						</div>
					</label>
				</FormField>
			)}
		</FormGrid>
	);

	// For new items, show just the form
	if (isNew) {
		return <CrudDetailPage title="New Budget Item" backLink="/budget-items" backLabel="All Budget Items" breadcrumb={[{ label: "Money", to: "/money" }, { label: "Budget Items", to: "/budget-items" }, { label: "New" }]} editForm={editForm} onSave={handleSave} isLoading={saving} variant="v4" icon={<FiTrendingUp />} />;
	}

	if (!item) {
		return <div style={{ padding: "20px" }}>Budget item not found</div>;
	}

	// Format recurrence for display
	const formatRecurrence = () => {
		const parts = [];

		if (item.specific_months) {
			const months = typeof item.specific_months === "string" ? JSON.parse(item.specific_months) : item.specific_months;
			if (months && months.length > 0) {
				const monthNames = months.map((m) => new Date(2000, m - 1).toLocaleString("default", { month: "long" }));
				parts.push(`Only in: ${monthNames.join(", ")}`);
			}
		}

		if (item.start_date) {
			parts.push(`From: ${new Date(item.start_date).toLocaleDateString()}`);
		}

		if (item.end_date) {
			parts.push(`Until: ${new Date(item.end_date).toLocaleDateString()}`);
		}

		return parts.length > 0 ? parts.join(" • ") : "Every month (recurring)";
	};

	// Detail view content
	const detailContent = (
		<>
			<DetailSection title="Item Details">
				<DetailGrid>
					<DetailField label="Section" value={item.section} />
					<DetailField label="Category" value={item.category || "—"} />
					<DetailField label="Name" value={item.name} />
					<DetailField
						label="Amount"
						value={
							<span style={{ color: item.is_income ? "#28a745" : "#666", fontWeight: 600 }}>
								{item.is_income ? "+" : "-"}
								{formatCurrency(Math.abs(item.amount))}
							</span>
						}
					/>
					<DetailField label="Type" value={item.is_income ? "Income" : "Expense"} />
					<DetailField label="Sort Order" value={item.sort_order} />
				</DetailGrid>
			</DetailSection>

			<DetailSection title="Recurrence">
				<p style={{ margin: 0, fontSize: "1.4rem" }}>{formatRecurrence()}</p>
			</DetailSection>

			{item.live_category_ref && (() => {
				let refs = item.live_category_ref;
				if (typeof refs === "string") {
					try {
						refs = JSON.parse(refs);
					} catch {
						refs = [];
					}
				}
				if (!refs || refs.length === 0) return null;
				return (
					<DetailSection title="Linked Spending Categories">
						<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
							{refs.map((ref) => (
								<span key={ref} style={{ padding: "4px 12px", background: "#e3f2fd", borderRadius: "4px", fontSize: "1.2rem" }}>
									{ref.replace(":", " > ")}
								</span>
							))}
						</div>
					</DetailSection>
				);
			})()}
		</>
	);

	return <CrudDetailPage title={item.name} backLink="/budget-items" backLabel="All Budget Items" breadcrumb={[{ label: "Money", to: "/money" }, { label: "Budget Items", to: "/budget-items" }, { label: item.name }]} detailContent={detailContent} editForm={editForm} onSave={handleSave} isLoading={saving} variant="v4" icon={<FiTrendingUp />} />;
};

export default BudgetItemEdit;
